import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  #itemListObserver;

  connectedCallback() {
    super.connectedCallback();

    if (this.hasAttribute('data-collection-schema')) {
      this.#syncItemList();
      this.#itemListObserver = new MutationObserver((mutations) => {
        const grid = this.querySelector('[ref="grid"]');
        const selector = '[ref="grid"], [data-collection-item-list]';
        const hasGridChanges = mutations.some(
          (mutation) =>
            mutation.type === 'attributes' ||
            mutation.target === grid ||
            [...mutation.addedNodes, ...mutation.removedNodes].some(
              (node) => node instanceof Element && (node.matches(selector) || node.querySelector(selector))
            )
        );

        if (hasGridChanges) this.#syncItemList();
      });
      this.#itemListObserver.observe(this, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-schema-name', 'data-schema-url'],
      });
    }

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#itemListObserver?.disconnect();
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  #syncItemList() {
    const script = this.querySelector('script[data-collection-item-list]');
    if (!script) return;

    const itemList = JSON.parse(script.textContent);
    itemList.itemListElement = Array.from(
      this.querySelectorAll('[ref="grid"] > [ref="cards[]"][data-schema-url]'),
      (card, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: card.dataset.schemaName,
        url: card.dataset.schemaUrl,
      })
    );
    itemList.numberOfItems = itemList.itemListElement.length;

    const json = JSON.stringify(itemList).replace(/</g, '\\u003c');
    if (script.textContent !== json) script.textContent = json;
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
